import { ToastContext } from "context/ToastContext";
import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  useCreatePracticeTypeSuggestionRuleMutation,
  useDeletePracticeTypeSuggestionRuleMutation,
  useGetPracticeTypeSuggestionRulesQuery,
  useUpdatePracticeTypeSuggestionRuleMutation,
} from "store/queries/practiceTypeSuggestion";
import styled from "styled-components";
import {
  IPracticeTypeSuggestionRule,
  IPracticeTypeSuggestionRuleSaveRequest,
} from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import { ServiceTypeRuleDialog } from "./ServiceTypeRuleDialog";
import { ServiceTypeRuleTester } from "./ServiceTypeRuleTester";
import {
  ALL_HOSPITALS,
  INTRO,
  MODALITY_LABELS,
  payersInWords,
  scopeInWords,
  serviceTypeLabel,
  serviceTypeName,
} from "./serviceTypeRuleWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
  .p-datatable-tbody > tr.service-type-rule-off > td {
    opacity: 0.55;
  }
`;

const chip = (text: string, className = "surface-100 text-color") => (
  <Tag key={text} value={text} className={`${className} font-normal`} />
);

const listOf = (data: any): any[] => (data as any)?.data || data || [];

/** The PUT body that re-saves a rule unchanged apart from `patch`. */
export const ruleToSaveRequest = (
  rule: IPracticeTypeSuggestionRule,
  patch: Partial<IPracticeTypeSuggestionRuleSaveRequest> = {}
): IPracticeTypeSuggestionRuleSaveRequest => ({
  accountId: rule.accountId,
  modality: rule.modality,
  payerIds: rule.payerIds,
  payerTypes: rule.payerTypes ?? [],
  planKeywords: rule.planKeywords,
  excludePlanKeywords: rule.excludePlanKeywords ?? [],
  targetPracticeTypeCode: rule.targetPracticeTypeCode,
  acceptedPracticeTypeCodes: rule.acceptedPracticeTypeCodes ?? [],
  reason: rule.reason,
  enabled: rule.enabled,
  priority: rule.priority,
  ...patch,
});

/** The filter's "every rule" choice. A Dropdown cannot hold null as a distinct option value. */
const EVERY_RULE = "__every__";

/**
 * CLE-2971 — Eligibility › Service Type Rules. When an estimate has an MRI, CT, ultrasound or X-ray
 * CPT code, these rules decide which Service Type (pVerify practice type code) its eligibility check
 * runs under. A rule is for ALL HOSPITALS (no account) or ONE hospital; at a hospital, its own enabled
 * rule for a procedure type replaces every all-hospitals rule for that type (the API's
 * `mergeRulesForAccount`). Mirrors the Payer Auto-Selection tab: same table, dialog, tester, toasts
 * and confirm; no drag-to-reorder, because the order is the `priority` number, edited in the dialog.
 */
export function ServiceTypeRulesTab() {
  const toast = useContext(ToastContext);
  const [filterAccountId, setFilterAccountId] = useState<string | null>(null);
  const {
    data: rules,
    isLoading,
    isError,
  } = useGetPracticeTypeSuggestionRulesQuery(filterAccountId);
  const { data: accountsData } = useGetAllAccountsQuery(null);
  const { data: payersData } = useGetAllPayersQuery();
  const [createRule, { isLoading: creating }] =
    useCreatePracticeTypeSuggestionRuleMutation();
  const [updateRule, { isLoading: updating }] =
    useUpdatePracticeTypeSuggestionRuleMutation();
  const [deleteRule] = useDeletePracticeTypeSuggestionRuleMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [testerOpen, setTesterOpen] = useState(false);
  const [editing, setEditing] = useState<IPracticeTypeSuggestionRule | null>(
    null
  );

  const accountNames = useMemo(
    () =>
      new Map<string, string>(
        listOf(accountsData).map((account: any) => [account.id, account.name])
      ),
    [accountsData]
  );
  const payerNames = useMemo(
    () =>
      new Map<number, string>(
        listOf(payersData).map((payer: any) => [payer.id, payer.name])
      ),
    [payersData]
  );
  const filterOptions = [
    { label: "Every rule", value: EVERY_RULE },
    ...sortBy(
      listOf(accountsData).map((account: any) => ({
        label: account.name,
        value: account.id,
      })),
      ["label"]
    ),
  ];

  const allRules = rules ?? [];
  const nextPriority =
    allRules.reduce((max, rule) => Math.max(max, rule.priority), 0) + 10;

  const fail = (summary: string, error: any, fallback: string) =>
    toast?.current?.show({
      severity: "error",
      summary,
      detail: error?.data?.message || error?.message || fallback,
    });

  const handleSave = async (body: IPracticeTypeSuggestionRuleSaveRequest) => {
    try {
      if (editing) {
        await updateRule({ id: editing.id, body }).unwrap();
      } else {
        await createRule(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Rule saved",
        detail: "The next eligibility check uses it.",
      });
    } catch (error: any) {
      fail("Not saved", error, "The rule could not be saved.");
    }
  };

  const toggle = async (
    rule: IPracticeTypeSuggestionRule,
    enabled: boolean
  ) => {
    try {
      await updateRule({
        id: rule.id,
        body: ruleToSaveRequest(rule, { enabled }),
      }).unwrap();
    } catch (error: any) {
      fail("Not changed", error, "The rule could not be changed.");
    }
  };

  const remove = async (rule: IPracticeTypeSuggestionRule) => {
    // eslint-disable-next-line no-alert
    const sure = window.confirm(
      `Remove the ${
        MODALITY_LABELS[rule.modality]
      } rule that runs under ${serviceTypeLabel(
        rule.targetPracticeTypeCode
      )} (${scopeInWords(
        rule,
        accountNames
      )})? The next eligibility check stops using it.`
    );
    if (!sure) return;
    try {
      await deleteRule(rule.id).unwrap();
    } catch (error: any) {
      fail("Not removed", error, "The rule could not be removed.");
    }
  };

  const openEditor = (rule: IPracticeTypeSuggestionRule | null) => {
    setEditing(rule);
    setDialogOpen(true);
  };

  /** Filtered to one hospital: the procedure types it has its own enabled rule for. */
  const ownModalities = new Set(
    filterAccountId
      ? allRules
          .filter((rule) => rule.accountId === filterAccountId && rule.enabled)
          .map((rule) => rule.modality)
      : []
  );

  const scopeBody = (rule: IPracticeTypeSuggestionRule) => (
    <div className="flex flex-column gap-1">
      <span
        className={rule.accountId === null ? "font-semibold" : ""}
        data-testid={`serviceTypeRules.rule.${rule.id}.scope`}
      >
        {scopeInWords(rule, accountNames)}
      </span>
      {rule.accountId === null && ownModalities.has(rule.modality) && (
        <small
          className="text-color-secondary"
          data-testid={`serviceTypeRules.rule.${rule.id}.shadowed`}
        >
          Replaced here by this hospital&apos;s own{" "}
          {MODALITY_LABELS[rule.modality]} rule
        </small>
      )}
    </div>
  );

  const modalityBody = (rule: IPracticeTypeSuggestionRule) => (
    <span
      className="font-semibold"
      data-testid={`serviceTypeRules.rule.${rule.id}`}
    >
      {MODALITY_LABELS[rule.modality] ?? rule.modality}
    </span>
  );

  const payersBody = (rule: IPracticeTypeSuggestionRule) => (
    <span data-testid={`serviceTypeRules.rule.${rule.id}.payers`}>
      {payersInWords(rule, payerNames)}
    </span>
  );

  const keywordsBody = (rule: IPracticeTypeSuggestionRule) => (
    <div
      className="flex flex-wrap gap-1 align-items-center"
      data-testid={`serviceTypeRules.rule.${rule.id}.keywords`}
    >
      {rule.planKeywords.length === 0 &&
        (rule.excludePlanKeywords ?? []).length === 0 && (
          <span className="text-color-secondary">Any plan</span>
        )}
      {rule.planKeywords.map((keyword) =>
        chip(keyword, "bg-indigo-100 text-indigo-900 font-mono")
      )}
      {(rule.excludePlanKeywords ?? []).map((keyword) =>
        chip(`not ${keyword}`, "bg-red-50 text-red-800 font-mono")
      )}
    </div>
  );

  const targetBody = (rule: IPracticeTypeSuggestionRule) => (
    <span
      className="font-medium"
      data-testid={`serviceTypeRules.rule.${rule.id}.target`}
    >
      {serviceTypeLabel(rule.targetPracticeTypeCode)}
    </span>
  );

  const acceptedBody = (rule: IPracticeTypeSuggestionRule) => (
    <span data-testid={`serviceTypeRules.rule.${rule.id}.accepted`}>
      {(rule.acceptedPracticeTypeCodes ?? []).length > 0 ? (
        (rule.acceptedPracticeTypeCodes ?? [])
          .map((code) => serviceTypeName(code))
          .join(", ")
      ) : (
        <span className="text-color-secondary">—</span>
      )}
    </span>
  );

  const enabledBody = (rule: IPracticeTypeSuggestionRule) => (
    <InputSwitch
      checked={rule.enabled}
      onChange={(event) => toggle(rule, !!event.value)}
      aria-label="Rule enabled"
      data-testid={`serviceTypeRules.rule.${rule.id}.enabled`}
    />
  );

  const reasonBody = (rule: IPracticeTypeSuggestionRule) => (
    <span className="text-sm">
      {rule.reason || <span className="text-color-secondary">—</span>}
    </span>
  );

  const actionsBody = (rule: IPracticeTypeSuggestionRule) => (
    <div className="flex gap-1 justify-content-end">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${MODALITY_LABELS[rule.modality]} rule`}
        data-testid={`serviceTypeRules.rule.${rule.id}.edit`}
        onClick={() => openEditor(rule)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Remove ${MODALITY_LABELS[rule.modality]} rule`}
        data-testid={`serviceTypeRules.rule.${rule.id}.remove`}
        onClick={() => remove(rule)}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" data-testid="serviceTypeRulesTab">
      <Surface>
        <div className="flex flex-wrap gap-2 mb-2 align-items-end">
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add rule"
            className="p-button-sm"
            data-testid="serviceTypeRules.add"
            onClick={() => openEditor(null)}
          />
          <Button
            type="button"
            icon="pi pi-play"
            label="Test an estimate"
            className="p-button-sm p-button-outlined"
            data-testid="serviceTypeRules.test"
            onClick={() => setTesterOpen(true)}
          />
          <div className="flex flex-column gap-1 ml-auto">
            <Label htmlFor="serviceTypeRules.filter">Show</Label>
            <Dropdown
              inputId="serviceTypeRules.filter"
              options={filterOptions}
              value={filterAccountId ?? EVERY_RULE}
              onChange={(event) =>
                setFilterAccountId(
                  !event.value || event.value === EVERY_RULE
                    ? null
                    : event.value
                )
              }
              filter
              style={{ minWidth: "16rem" }}
            />
            {filterAccountId && (
              <small className="text-color-secondary">
                This hospital&apos;s rules and the {ALL_HOSPITALS.toLowerCase()}{" "}
                rules.
              </small>
            )}
          </div>
        </div>
        <p className="mt-0 mb-3 text-color-secondary">{INTRO}</p>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="serviceTypeRules.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The Service Type rules could not be loaded."
            data-testid="serviceTypeRules.error"
          />
        )}
        {!isLoading && !isError && allRules.length === 0 && (
          <span
            className="text-color-secondary"
            data-testid="serviceTypeRules.empty"
          >
            No rules yet, so no eligibility check changes its Service Type.
          </span>
        )}
        {!isLoading && !isError && allRules.length > 0 && (
          <StyledDataTable
            value={allRules}
            dataKey="id"
            rowClassName={(rule: IPracticeTypeSuggestionRule) => ({
              "service-type-rule-off": !rule.enabled,
            })}
            className="w-full"
            data-testid="serviceTypeRules.table"
          >
            <Column
              header="Scope"
              body={scopeBody}
              style={{ width: "11rem" }}
            />
            <Column
              header="Procedure type"
              body={modalityBody}
              style={{ width: "8rem" }}
            />
            <Column
              header="Payers"
              body={payersBody}
              style={{ width: "12rem" }}
            />
            <Column
              header="Plan keywords"
              body={keywordsBody}
              style={{ width: "14rem", maxWidth: "14rem" }}
            />
            <Column
              header="Runs under"
              body={targetBody}
              style={{ width: "12rem" }}
            />
            <Column
              header="Also accepted"
              body={acceptedBody}
              style={{ width: "10rem" }}
            />
            <Column
              header="Enabled"
              body={enabledBody}
              style={{ width: "6rem" }}
            />
            <Column
              header="Priority"
              field="priority"
              style={{ width: "5rem" }}
            />
            <Column header="Reason" body={reasonBody} />
            <Column body={actionsBody} style={{ width: "6rem" }} />
          </StyledDataTable>
        )}
      </Surface>

      <ServiceTypeRuleTester
        visible={testerOpen}
        onHide={() => setTesterOpen(false)}
      />

      <ServiceTypeRuleDialog
        visible={dialogOpen}
        rule={editing}
        defaultAccountId={filterAccountId}
        saving={creating || updating}
        nextPriority={nextPriority}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </div>
  );
}
